import React, { useState, useEffect } from "react";
import { X, ArrowRight, Sparkles, CalendarClock } from "lucide-react";
import { services, brand } from "../mock";
import { useToast } from "../hooks/use-toast";

const BookingModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    timing: "",
    message: "",
  });

  // Load Calendly script + stylesheet once
  useEffect(() => {
    if (document.getElementById("calendly-widget-script")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.id = "calendly-widget-script";
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const openCalendly = () => {
    if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
      window.Calendly.initPopupWidget({ url: brand.calendlyUrl });
    } else {
      window.open(brand.calendlyUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({
        title: "Almost there",
        description: "Please share your name and email so I can respond.",
      });
      return;
    }
    toast({
      title: "Your journey has begun",
      description: `Thank you, ${form.name.split(" ")[0]}. I'll be in touch within two business days to confirm your consultation.`,
    });
    setForm({ name: "", email: "", phone: "", service: "", timing: "", message: "" });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#0A0714]/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[#D4A85B]/40 bg-[#12081E] shadow-[0_40px_100px_-20px_rgba(212,168,91,0.35)]">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute -top-24 -left-16 w-[300px] h-[300px] rounded-full bg-[#A36CFF]/25 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-[320px] h-[320px] rounded-full bg-[#D4A85B]/15 blur-[100px]" />

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#D4A85B]/40 text-[#EDE3D0] hover:bg-[#D4A85B] hover:text-[#0A0714] flex items-center justify-center transition z-10"
        >
          <X size={16} />
        </button>

        <div className="relative p-7 md:p-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/85">
            <Sparkles size={12} /> Begin your journey
          </div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight mt-4 text-[#EDE3D0]">
            Book a
            <span className="italic text-[#D4A85B]"> complimentary consultation.</span>
          </h2>
          <p className="mt-3 text-sm text-[#EDE3D0]/70 leading-relaxed max-w-md">
            Share a little about where you are and what you&apos;re seeking. I&apos;ll personally
            respond within two business days to schedule a discovery call.
          </p>

          {/* Calendly Consultation CTA */}
          <div className="mt-6 rounded-2xl border border-[#D4A85B]/40 bg-gradient-to-br from-[#1A0E30] to-[#0F0821] p-5 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="w-12 h-12 rounded-full bg-[#D4A85B]/15 border border-[#D4A85B]/40 flex items-center justify-center text-[#D4A85B] shrink-0">
                <CalendarClock size={20} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">Fastest way in</div>
                <div className="font-serif text-xl text-[#EDE3D0] mt-1 leading-tight">
                  Book a complimentary 15-minute consultation
                </div>
                <div className="text-sm text-[#EDE3D0]/65 mt-1">
                  Instant self-scheduling on my live calendar.
                </div>
              </div>
              <button
                type="button"
                onClick={openCalendly}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-5 py-3 text-[11px] uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition whitespace-nowrap shrink-0"
              >
                Choose a time <ArrowRight size={13} />
              </button>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.28em] text-[#EDE3D0]/50">
            <div className="flex-1 h-px bg-[#D4A85B]/20" />
            <span>Or send a note</span>
            <div className="flex-1 h-px bg-[#D4A85B]/20" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                  Your name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  type="text"
                  className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                  placeholder="Devona"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                  Email
                </label>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  type="email"
                  className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                  placeholder="you@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                  Phone (optional)
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  type="tel"
                  className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                  placeholder="(555) 000-0000"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                  Best times to reach you
                </label>
                <input
                  value={form.timing}
                  onChange={(e) => setForm({ ...form, timing: e.target.value })}
                  type="text"
                  className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40"
                  placeholder="Weekdays after 5pm ET"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                I am drawn to
              </label>
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0]"
              >
                <option value="" className="bg-[#12081E]">
                  Choose an offering (optional)
                </option>
                {services.map((s) => (
                  <option key={s.id} value={s.title} className="bg-[#12081E]">
                    {s.title} · {s.price}
                  </option>
                ))}
                <option value="Not sure yet" className="bg-[#12081E]">
                  I&apos;m not sure yet
                </option>
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85">
                What&apos;s on your heart?
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40 resize-none"
                placeholder="Tell me where you are, what you're carrying, and what you're hoping for."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <div className="text-xs text-[#EDE3D0]/55">
                Your information is private and never shared. Consultations held virtually.
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition whitespace-nowrap"
              >
                Book my consultation <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
